import { styled } from '@mui/system';
import { useContext, useEffect, useState } from 'react';
import { useForm } from "react-hook-form";

import { DataContext } from 'providers/data';
import * as Regex from 'utils/validations';
import { Box, Typography } from '@mui/material';

import Modals from 'components/views/Modals';
import Input from 'components/views/form/Input';
import { ButtonRectangle, ButtonOutlined } from 'components/views/form/Button';

import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';

export const LinkExtermal = styled('a')(`
    font-size: .9375rem;
    position: relative;
    font-weight: 700;
    color: white;
    display: flex;
    height: 40px;
    border-radius: 5px;
    align-items: center;
    justify-content: center;
    border: 2px solid #FFFFFF;
    margin-bottom: .9375rem;
    
    svg {
        position: absolute;
        right: 1.25rem;
        height: 1.2rem;
    }
`);

type Params = {
    open: boolean;
    handleClick: (value: boolean) => void;
}

export const ModalConfirmation = ({ open, handleClick }: Params) => {
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdatePhone, setLastUpdatePhone] = useState('');

    const { dataFormPersonal, dataValidatePersonalData, handlePostValidatePersonalData } = useContext(DataContext);
    const { register, handleSubmit, setValue, watch } = useForm<{ email: string; phone: string }>();

    const [email, phone] = watch(['email', 'phone']);

    const handleOnSubmit = () => {
        const tokenExternal = localStorage.getItem('token_external');

        if (dataFormPersonal) {
            setIsLoading(true);
            handlePostValidatePersonalData({ body: dataFormPersonal, token: tokenExternal! });
        }
    }

    useEffect(() => {
        if (dataFormPersonal) {
            setValue('email', dataFormPersonal.DS_EMAIL);
            setValue('phone', `${dataFormPersonal.Telefone.NR_DDD}${dataFormPersonal.Telefone.NR_TELEFONE}`);
            const datePhone = dataFormPersonal.Telefone.DT_ALTERACAO.split('T')[0].split('-');
            setLastUpdatePhone(`${datePhone[2]}-${datePhone[1]}-${datePhone[0]}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataFormPersonal])

    useEffect(() => {
        if (typeof dataValidatePersonalData !== 'undefined') {
            if (dataValidatePersonalData) {
                handleClick(true);
            } else {
                handleClick(false);
            }

            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataValidatePersonalData]);

    return (
        <Modals open={open}>
            <Box sx={{ borderRadius: '8px', width: { xs: 'calc(100vw - 2rem)', md: '23.375rem' } }} component='form' onSubmit={handleSubmit(handleOnSubmit)}>
                <Box borderRadius='8px 8px 0 0' textAlign='center' padding='1.6rem 0 .5rem'>
                    <Typography fontSize='1.5625rem' fontWeight={700}>
                        ATENÇÃO!
                    </Typography>
                    <Typography fontSize='.9375rem' lineHeight='127%' paddingTop='1rem' paddingX='1rem' marginBottom='1rem'>
                        <b>Confira os dados</b> abaixo se estão corretos, enviaremos o <b>token</b> via email/sms:
                    </Typography>

                    <Box sx={{ paddingX: { xs: '1.66rem', md: '1.81rem' } }}>
                        <Input
                            name='email'
                            label='E-mail cadastrado'
                            placeholder='seuemail@email.com'
                            type='text'
                            disabled
                            register={{ ...register("email", { pattern: Regex.email }) }}
                        />
                        <Input
                            name='phone'
                            label='Celular'
                            placeholder='(99) 99999-9999'
                            maskHolder='(99) 99999-9999'
                            type='tel'
                            mask
                            disabled
                            register={{ ...register("phone", { pattern: Regex.phone }) }}
                        />
                        {lastUpdatePhone &&
                            <Typography fontSize='.75rem' textAlign='left' sx={{
                                marginTop: { xs: '-1.2rem', md: '-.5rem' }
                            }}>
                                Atualizado: {lastUpdatePhone}
                            </Typography>
                        }
                    </Box>
                </Box>
                <Box bgcolor='primary.main' borderRadius='0 0 8px 8px' display='grid' rowGap='.9rem' sx={{
                    padding: {
                        xs: '1.81rem 1.66rem 2.21rem',
                        md: '1.81rem 1.81rem 2.21rem'
                    }
                }}>
                    <ButtonRectangle name='GERAR TOKEN' type='submit' loadingBtn={isLoading} />
                    <ButtonOutlined name='FECHAR' disabled={isLoading} onClick={() => handleClick(false)} isWhiteOutline type='button' />
                </Box>
            </Box>
        </Modals>
    );
}

export const ModalDefault = ({ open, handleClick }: Params) => {
    return (
        <Modals open={open}>
            <Box sx={{ borderRadius: '8px', width: { xs: 'calc(100vw - 2rem)', md: '24rem' } }}>
                <Box borderRadius='8px 8px 0 0' paddingTop='1.6rem' textAlign='center'>
                    <Typography fontSize='1.5625rem' fontWeight={700}>
                        ATENÇÃO!
                    </Typography>
                    <Typography fontSize='.9375rem' lineHeight='127%' paddingTop='1rem' paddingX='1rem' marginBottom='1rem'>
                        Tivemos um problema ao validar as informações. <br />
                        Entre em contato com a nossa<br />
                        Central de Atendimento:
                    </Typography>
                </Box>
                <Box bgcolor='primary.main' borderRadius='0 0 8px 8px' display='grid' sx={{
                    padding: {
                        xs: '1.81rem 1.66rem 2.21rem',
                        md: '1.81rem 1.81rem 2.21rem'
                    }
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
                            <Typography fontSize='.75rem' color='white'>
                                São Paulo
                            </Typography>
                            <Typography fontSize='.75rem' color='white'>
                                Digite a opção 1,2
                            </Typography>
                        </Box>
                        <LinkExtermal href="tel:1136169000">
                            (11) 3616-9000
                            <LocalPhoneOutlinedIcon />
                        </LinkExtermal>
                    </Box>
                    <Box marginBottom='.5rem'>
                        <Typography fontSize='.75rem' color='white' paddingBottom='.2rem' textAlign='start'>
                            Outras localidades
                        </Typography>
                        <LinkExtermal href="tel:08007500044">
                            0800 750 0044
                            <LocalPhoneOutlinedIcon />
                        </LinkExtermal>
                    </Box>
                    <ButtonOutlined name='FECHAR' onClick={() => handleClick(false)} isWhiteOutline type='button' />
                </Box>
            </Box>
        </Modals>
    );
}

export const ModalError = ({ open, handleClick }: Params) => {
    return (
        <Modals open={open}>
            <Box sx={{ borderRadius: '8px', width: { xs: 'calc(100vw - 2rem)', md: '23.375rem' } }}>
                <Box borderRadius='8px 8px 0 0' paddingTop='1.6rem' textAlign='center'>
                    <Typography fontSize='1.5625rem' fontWeight={700}>
                        ATENÇÃO!
                    </Typography>
                    <Typography fontSize='.9375rem' lineHeight='127%' paddingTop='1rem' paddingX='1rem' marginBottom='1rem'>
                        Os dados informados estão <b>incorretos.</b> <br />
                        Por favor, revise e tente novamente.
                    </Typography>
                </Box>
                <Box bgcolor='primary.main' borderRadius='0 0 8px 8px' display='grid' sx={{
                    padding: {
                        xs: '1.81rem 1.66rem 2.21rem',
                        md: '1.81rem 1.81rem 2.21rem'
                    }
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '.2rem' }}>
                            <Typography fontSize='.75rem' color='white'>
                                São Paulo
                            </Typography>
                            <Typography fontSize='.75rem' color='white'>
                                Digite a opção 1,2
                            </Typography>
                        </Box>
                        <LinkExtermal href="tel:1136169000">
                            (11) 3616-9000
                            <LocalPhoneOutlinedIcon />
                        </LinkExtermal>
                    </Box>
                    <Box marginBottom='.5rem'>
                        <Typography fontSize='.75rem' color='white' paddingBottom='.2rem' textAlign='start'>
                            Outras localidades
                        </Typography>
                        <LinkExtermal href="tel:08007500044">
                            0800 750 0044
                            <LocalPhoneOutlinedIcon />
                        </LinkExtermal>
                    </Box>
                    <ButtonOutlined name='FECHAR' onClick={() => handleClick(false)} isWhiteOutline type='button' />
                </Box>
            </Box>
        </Modals>
    );
}

export const ModalZipCodeOvernight = ({ open, handleClick }: Params) => {
    return (
        <Modals open={open}>
            <Box sx={{ borderRadius: '8px', width: { xs: 'calc(100vw - 2rem)', md: '23.375rem' } }}>
                <Box borderRadius='8px 8px 0 0' paddingTop='1.6rem' textAlign='center'>
                    <Typography fontSize='1.875rem' fontWeight={700}>
                        ATENÇÃO!
                    </Typography>
                    <Box padding='1.3rem 1rem 1.5rem'>
                        <Typography lineHeight='1.26' fontSize='1rem'>
                            Essa alteração se aplica <b>apenas</b> ao seu <br />
                            endereço de cobrança, não alterando <br />
                            sua cotação.
                        </Typography>
                    </Box>
                </Box>
                <Box bgcolor='primary.main' borderRadius='0 0 8px 8px' display='grid' padding='1.375rem 3.5rem 1.68rem'>
                    <ButtonRectangle name='ESTOU CIENTE' type='button' onClick={() => handleClick(false)} />
                </Box>
            </Box>
        </Modals>
    );
}
